// SlipSyncSettings.jsx — sync button, state display, result summary, error messages
// Spec: docs/specs/slipsync-ui.md Section 3

import { useSlipSync } from '../../hooks/useSlipSync';
import StateSetupModal from './StateSetupModal';
import SlipSyncConsentSheet from './SlipSyncConsentSheet';
import { SYNC_STATUS } from '../../lib/slipsync/constants';
import { JT } from '../../components/ui/jumbotronTokens';
import { mgmConfig } from '../../lib/slipsync/books/mgm';

function timeAgo(isoString) {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function SlipSyncSettings({ user, onSignIn, onSyncComplete, book = mgmConfig }) {
  const {
    syncStatus, errorMessage, resultSummary, lastSyncedAt, needsReauth,
    triggerSync, showStateSetup, saveStateConfig, configuredState, isIos,
    syncDiagnostics, syncNotice, reconnectInfo, canReconnect, reconnect,
    showConsent, consentBusy, consentError, acceptConsent, declineConsent,
  } = useSlipSync({ user, onSyncComplete, book });

  if (!isIos) return null;

  const isActive = syncStatus === SYNC_STATUS.OPENING_WEBVIEW
    || syncStatus === SYNC_STATUS.SUBMITTING;
  const showButton = syncStatus === SYNC_STATUS.IDLE
    || syncStatus === SYNC_STATUS.SUCCESS
    || syncStatus === SYNC_STATUS.ERROR;

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#f1f5f9', marginBottom: 16 }}>
        {book.displayName} Sync
      </div>

      {configuredState && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
          State: <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{configuredState.toUpperCase()}</span>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 16 }}>
        Last synced: {timeAgo(lastSyncedAt)}
      </div>

      {!user && onSignIn && (
        <button
          onClick={onSignIn}
          style={{
            background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)',
            border: 'none', borderRadius: 10, padding: '12px 24px',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginBottom: 16,
          }}
        >
          Sign in to sync
        </button>
      )}

      {user && showButton && (
        <button
          onClick={triggerSync}
          disabled={isActive}
          style={{
            background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)',
            border: 'none', borderRadius: 10, padding: '12px 24px',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: isActive ? 'not-allowed' : 'pointer',
            opacity: isActive ? 0.5 : 1,
            fontFamily: 'inherit', width: '100%', marginBottom: 16,
          }}
        >
          Sync {book.displayName} Bets
        </button>
      )}

      {isActive && (
        <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', marginBottom: 16 }}>
          {syncStatus === SYNC_STATUS.OPENING_WEBVIEW && `Connecting to ${book.displayName}...`}
          {syncStatus === SYNC_STATUS.SUBMITTING && 'Submitting bets...'}
          {/* Bridge hardening (spec §7): say the sync is starting while the window opens. */}
          {syncNotice && syncStatus === SYNC_STATUS.OPENING_WEBVIEW && (
            <div role="status" style={{ fontSize: 12, color: JT.text.secondary, marginTop: 6 }}>{syncNotice}</div>
          )}
        </div>
      )}

      {syncStatus === SYNC_STATUS.ERROR && errorMessage && (
        <div style={{
          background: '#1a0000', border: '1px solid #7f1d1d', borderRadius: 8,
          padding: '10px 14px', fontSize: 12, color: '#fca5a5', marginBottom: 12,
        }}>
          {errorMessage}
        </div>
      )}

      {/* Bridge hardening (spec §7, P1-4): Reconnect clears the failure ladder for the refused book only. */}
      {reconnectInfo && (
        <button type="button" onClick={reconnect} disabled={!canReconnect}
          style={{ minHeight: 44, marginBottom: 12, padding: '10px 16px', borderRadius: 8, border: `1px solid ${JT.border.mid}`, background: 'none',
            color: canReconnect ? JT.text.primary : JT.text.tertiary, fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: canReconnect ? 'pointer' : 'default' }}>
          Reconnect
        </button>
      )}

      {syncStatus === SYNC_STATUS.ERROR && syncDiagnostics && syncDiagnostics.length > 0 && (
        <div style={{
          background: '#0a0a1a', border: '1px solid #1e293b', borderRadius: 8,
          padding: '10px 14px', fontSize: 10, color: '#64748b', marginBottom: 12,
          fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {syncDiagnostics.map((d, i) => {
            const parts = [`${d.t}ms ${d.event}`];
            if (d.trigger) parts.push(d.trigger);
            if (d.type) parts.push(d.type);
            if (d.reason) parts.push(d.reason);
            if (typeof d.error === 'string' && d.error) parts.push(d.error.slice(0, 100)); // stage code (20) + errText (80)
            if (d.url) parts.push(String(d.url).slice(0, 60));
            if (d.resultType) parts.push(`-> ${d.resultType}`);
            parts.push(`[${d.attempts}]`);
            return parts.join(' ');
          }).join('\n')}
        </div>
      )}

      {needsReauth && onSignIn && (
        <button
          onClick={onSignIn}
          style={{
            background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)',
            border: 'none', borderRadius: 10, padding: '12px 24px',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginBottom: 16,
          }}
        >
          Sign in again
        </button>
      )}

      {syncStatus === SYNC_STATUS.SUCCESS && resultSummary && (
        <div style={{
          background: '#001a0a', border: '1px solid #14532d', borderRadius: 8,
          padding: '10px 14px', fontSize: 12, color: '#86efac', marginBottom: 12,
        }}>
          {resultSummary.unknown
            ? 'Sync completed but result unknown.'
            : `Imported: ${resultSummary.imported} · Skipped: ${resultSummary.skipped} · Failed: ${resultSummary.failed}${resultSummary.untracked > 0 ? ` · Not tracked: ${resultSummary.untracked}` : ''}`}
        </div>
      )}

      {showConsent && (
        <SlipSyncConsentSheet onAgree={acceptConsent} onDecline={declineConsent} busy={consentBusy} error={consentError} />
      )}
      {showStateSetup && (
        <StateSetupModal onSave={saveStateConfig} />
      )}
    </div>
  );
}
